import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEO from "../components/SEO";
import { Zap, Clock, ShieldCheck, Info, Check, Gift, ClipboardCheck, Headphones, BadgeCheck, Quote, Lock } from "lucide-react";

const POPULAR_BRANDS = [
  "Amazon", "Apple", "Visa", "Walmart", "Target", "Best Buy",
  "Nike", "Sephora", "Steam", "Google Play", "Home Depot", "Lululemon",
];

const SECURITY_CARDS = [
  { img: "/jumbo/images/Group-9181.svg", title: "Safe and secure", text: "Card details are encrypted in transit and at rest, and never shared with third parties." },
  { img: "/jumbo/images/customizable-icon.svg", title: "Human verification", text: "A real reviewer checks every card. That's how we keep rates high and fraud out." },
  { img: "/jumbo/images/browser-icon.svg", title: "Live tracking", text: "Every submission gets a SPARK reference — track it any time with just your email." },
];

const FEATURE_TABS = [];

const PAYOUT_TIERS = [
  {
    tone: "fast",
    accent: "#16a34a",
    accentSoft: "#e8f7ee",
    label: "Most cards",
    share: "~ 70%",
    time: "24 – 48 hours",
    Icon: Zap,
    line1: "Clean verifications that clear our automated checks — you get paid within a day or two.",
    detail: "Auto-verified",
  },
  {
    tone: "mid",
    accent: "#d97706",
    accentSoft: "#fdf2df",
    label: "Some cards",
    share: "~ 20%",
    time: "3 – 5 business days",
    Icon: Clock,
    line1: "Cards that need deeper balance & fraud checks before we release payment.",
    detail: "Extended review",
  },
  {
    tone: "slow",
    accent: "#2563eb",
    accentSoft: "#e6ecff",
    label: "A few cards",
    share: "~ 10%",
    time: "5 – 9 business days",
    Icon: ShieldCheck,
    line1: "Flagged submissions that go through a manual security review to protect every seller.",
    detail: "Manual review",
  },
];

function PayoutTimeSection() {
  return (
    <section className="payout-time-section" data-testid="home-payout-time">
      <div className="main-container">
        <div className="payout-time-header">
          <span className="payout-time-eyebrow" data-testid="payout-time-eyebrow">Estimated payout time</span>
          <h2 className="h2 _700-bold payout-time-heading" data-testid="payout-time-heading">
            When does the money land in your account?
          </h2>
          <p className="payout-time-sub" data-testid="payout-time-sub">
            Gift-card resale is a sensitive category, so verification depth varies from card to card.
            Most sellers are paid within 24 – 48 hours. Here&rsquo;s the honest breakdown so you always know what to expect.
          </p>
        </div>

        <div className="payout-tier-grid" data-testid="payout-tier-grid">
          {PAYOUT_TIERS.map((t) => {
            const Icon = t.Icon;
            return (
              <article
                key={t.tone}
                className={`payout-tier-card payout-tier-${t.tone}`}
                style={{ "--tier-accent": t.accent, "--tier-accent-soft": t.accentSoft }}
                data-testid={`payout-tier-${t.tone}`}
              >
                <div className="payout-tier-top">
                  <span className="payout-tier-icon" aria-hidden="true">
                    <Icon size={22} strokeWidth={2.2} />
                  </span>
                  <span className="payout-tier-share">{t.share}</span>
                </div>
                <div className="payout-tier-label">{t.label}</div>
                <div className="payout-tier-time">{t.time}</div>
                <p className="payout-tier-desc">{t.line1}</p>
                <div className="payout-tier-detail">
                  <span className="payout-tier-detail-dot" />
                  {t.detail}
                </div>
                <div className="payout-tier-bar">
                  <span className="payout-tier-bar-fill" />
                </div>
              </article>
            );
          })}
        </div>

        <div className="payout-time-footnote" data-testid="payout-time-footnote">
          <span className="payout-time-footnote-icon" aria-hidden="true">
            <Info size={18} strokeWidth={2.2} />
          </span>
          <div>
            <strong>Why timing varies.</strong> Some issuers throttle balance checks and require manual review to prevent chargebacks.
            Your estimate is <em>shown at submission</em> — final payout is confirmed after human verification.
          </div>
        </div>
      </div>
    </section>
  );
}

const PAYOUT_CHECKS = [
  "PayPal",
  "Cash App",
  "Zelle",
  "Secure payout process",
  "Track your transaction status",
  "Dedicated customer support",
];

const REVIEWS = [
  { initials: "SM", name: "Sarah M.", location: "Denver, CO", brand: "Target Gift Card", rating: 5, date: "July 2026", text: "The submission process was easy and I appreciated being able to track my transaction from start to finish. Clear updates the whole way." },
  { initials: "MT", name: "Marcus T.", location: "Chicago, IL", brand: "Best Buy Gift Card", rating: 5, date: "June 2026", text: "Everything was explained clearly before I submitted. No surprises, and the status tracker made it easy to know where things stood." },
  { initials: "JK", name: "Jasmine K.", location: "Atlanta, GA", brand: "Sephora Gift Card", rating: 5, date: "June 2026", text: "I liked being able to check the payout estimate before submitting. Support answered a follow-up question within a few hours." },
  { initials: "DW", name: "Derek W.", location: "Portland, OR", brand: "Home Depot Gift Card", rating: 4, date: "May 2026", text: "The process was smoother than I expected. A rep double-checked my payout details after I made a typo — appreciated the human touch." },
  { initials: "PR", name: "Priya R.", location: "Austin, TX", brand: "Nike Gift Card", rating: 5, date: "May 2026", text: "Uploaded a couple of cards, verification came back within the day, and payout arrived at the account I selected." },
  { initials: "AL", name: "Alex L.", location: "Brooklyn, NY", brand: "Google Play Gift Card", rating: 5, date: "April 2026", text: "Straightforward interface. I could see exactly which step my submission was on. Would use again for cards I'm not going to spend." },
  { initials: "MG", name: "Maria G.", location: "Miami, FL", brand: "Walmart Gift Card", rating: 4, date: "April 2026", text: "Support was patient walking me through what a receipt does to the estimate. I liked that they explained it instead of just quoting a number." },
  { initials: "TC", name: "Tyler C.", location: "Seattle, WA", brand: "Apple Gift Card", rating: 5, date: "March 2026", text: "Clean submission flow, honest estimate range, and the payment landed in the account I chose without any back-and-forth." },
];

const FEATURED_REVIEW = {
  initials: "JT",
  name: "James Thornton",
  location: "Raleigh, NC",
  brand: "Amazon Gift Card",
  payout: "PayPal",
  rating: 5,
  date: "June 2026",
  text: "Sold my Amazon gift card without any issues. The submission process was straightforward and I received updates throughout the review. When I had a question about my payout account, someone from support got back to me the same afternoon — felt like a real team, not a chatbot.",
};

const RATING_BREAKDOWN = [
  { label: "Process",  score: 4.9 },
  { label: "Support",  score: 4.8 },
  { label: "Overall",  score: 4.8 },
];

/** Render 0-5 stars (supports halves). Uses inline SVG to avoid extra deps. */
function Stars({ value = 5, size = 14, gap = 2, color = "#f5a524" }) {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const items = Array.from({ length: 5 }, (_, i) => (i < full ? "full" : i === full && half ? "half" : "empty"));
  return (
    <span aria-label={`${value} out of 5 stars`} style={{ display: "inline-flex", gap }}>
      {items.map((kind, i) => (
        <svg key={i} width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
          <defs>
            <linearGradient id={`half-${i}-${Math.round(value*10)}`} x1="0" x2="1" y1="0" y2="0">
              <stop offset="50%" stopColor={color} />
              <stop offset="50%" stopColor="#e6e8f2" />
            </linearGradient>
          </defs>
          <path
            d="M10 1.5l2.75 5.57 6.15.9-4.45 4.34 1.05 6.13L10 15.6l-5.5 2.84 1.05-6.13L1.1 7.97l6.15-.9L10 1.5z"
            fill={kind === "full" ? color : kind === "half" ? `url(#half-${i}-${Math.round(value*10)})` : "#e6e8f2"}
          />
        </svg>
      ))}
    </span>
  );
}

const FAQS = [
  { q: "How fast do I get paid?", a: "Most cards are verified and paid within 24 hours of approval. Complex cards can take a few business days. You get an email at every step, and you can track your submission live with your SPARK reference." },
  { q: "Which payout methods do you support?", a: "PayPal, Cash App and Zelle. You pick your preferred method during submission and we pay to exactly the account you provide." },
  { q: "Do I need an account to sell?", a: "No. You can sell as a guest — your SPARK reference number plus a one-time email code is enough to track your submission and contact support." },
  { q: "How much will I get for my card?", a: "It depends on the brand and whether you have a receipt — up to 93% of the balance. Use the rate checker to see your exact quote before you submit anything." },
];

export default function HomePage() {
  const navigate = useNavigate();
  const [topRates, setTopRates] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [heroEmail, setHeroEmail] = useState("");

  useEffect(() => {
    api
      .get("/brands")
      .then((r) => {
        const rows = (r.data || []).filter((b) => b.active !== false);
        // Feature these four brands in order when available; otherwise fall back to top 4 by rate.
        const featured = ["adidas", "amazon", "best-buy", "costco"];
        const byKey = new Map(rows.map((b) => [(b.slug || b.name || "").toLowerCase().replace(/\s+/g, "-"), b]));
        const picked = featured.map((k) => byKey.get(k)).filter(Boolean);
        const chosen = picked.length === featured.length
          ? picked
          : rows.sort((a, b) => ((b.rates?.receipt || b.rate || 0) - (a.rates?.receipt || a.rate || 0))).slice(0, 4);
        setTopRates(chosen);
      })
      .catch(() => {});
  }, []);

  const heroSubmit = (e) => {
    e.preventDefault();
    navigate(heroEmail.trim() ? `/submit?email=${encodeURIComponent(heroEmail.trim())}` : "/submit");
  };

  return (
    <>
      <SEO
        title="Sell Gift Cards for Cash"
        description="GC SPARK turns unused gift cards into cash. Upload your card, get verified, get paid via PayPal, Cash App, Zelle or Chime — typically within 24 hours of approval."
      />

      {/* ── 1. Hero ─────────────────────────────────────── */}
      <section id="Home" className="home-hero-section" data-testid="home-hero">
        <div className="main-container w-container">
          <div className="home-hero-container">
            <div className="home-hero-text-container">
              {/* Trust badge above heading */}
              <div className="hero-trust-badge" data-testid="hero-trust-badge">
                <span className="hero-trust-dot" aria-hidden="true" />
                Human review · transparent verification
              </div>

              <div className="home-hero-heading">
                <h1 className="h1 _700-bold" data-testid="hero-h1">
                  Sell Your Unused <span style={{ color: "#346af8" }}>Gift Cards Online</span>
                </h1>
                <div className="home-hero-subtitle" data-testid="hero-subtitle">
                  Fast, Secure, Reliable Payouts
                </div>
              </div>
              <div className="home-hero-para">
                <p data-testid="hero-supporting-text">
                  Submit your gift card, complete our verification process, and receive your payout
                  once your card is verified and approved — typically within 24 hours of approval.
                </p>
              </div>

              {/* Benefits row */}
              <ul className="hero-benefits-row" data-testid="hero-benefits-row" aria-label="Why sell with GC SPARK">
                {[
                  "Secure transaction process",
                  "Competitive exchange rates",
                  "Real human verification",
                  "Track your submission anytime",
                ].map((b) => (
                  <li key={b} className="hero-benefit-item">
                    <span className="hero-benefit-check" aria-hidden="true"><Check className="w-3 h-3" strokeWidth={3} /></span>
                    <span className="hero-benefit-text">{b}</span>
                  </li>
                ))}
              </ul>

              {/* CTAs */}
              <div className="hero-cta-row" data-testid="hero-cta-row">
                <Link to="/submit" className="hero-cta-primary" data-testid="hero-cta-primary">
                  Sell a Gift Card
                </Link>
                <Link to="/rates" className="hero-cta-secondary" data-testid="hero-cta-secondary">
                  Check Live Rates
                </Link>
              </div>
            </div>
            <figure className="home-hero-image-wrapper">
              <picture>
                <source srcSet="/jumbo/images/hero-card-submitted.webp" type="image/webp" />
                <img
                  src="/jumbo/images/hero-card-submitted.png"
                  loading="eager"
                  fetchpriority="high"
                  alt="Card submitted successfully on GC SPARK"
                  className="home-hero-image"
                  data-testid="home-hero-image"
                />
              </picture>
            </figure>
          </div>
        </div>
      </section>

      {/* ── 2. Trust section ────────────────────────────── */}
      <section className="trust-section" data-testid="home-trust">
        <div className="main-container w-container">
          <div className="trust-grid">
            <div className="trust-heading-col">
              <span className="trust-eyebrow">Why choose us</span>
              <h2 className="trust-heading">Built for a smarter way to <span style={{ color: "#ffd166" }}>sell gift cards</span></h2>
              <p className="trust-subheading">Every step of your gift-card sale is designed around transparency and trust — from submission to payout.</p>
            </div>
            <div className="trust-features-col">
              {[
                { Icon: ShieldCheck, title: "Secure Transactions", desc: "Every eligible gift card is reviewed through our verification process before approval." },
                { Icon: Gift,        title: "70+ Brands Accepted", desc: "Sell gift cards from a wide range of popular retailers on one platform." },
                { Icon: ClipboardCheck, title: "Transparent Process", desc: "Track your submission from review through payout with clear status updates." },
                { Icon: Headphones,  title: "Dedicated Support",  desc: "Our team is available to assist you throughout your transaction whenever you need help." },
              ].map((f) => (
                <article className="trust-feature" key={f.title} data-testid={`trust-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="trust-feature-icon" aria-hidden="true">
                    <f.Icon strokeWidth={1.7} className="w-6 h-6" />
                  </div>
                  <h3 className="trust-feature-title">{f.title}</h3>
                  <p className="trust-feature-desc">{f.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 3. Feature tabs + why-us rows ───────────────── */}
      <section className="feature-section" data-testid="home-features">
        <div className="anchorlink-space" id="Features" />
        <div className="main-container w-container">
          <div className="feature-container">
            <div className="feature-row-wrapper">
              {/* Row 2 — know your payout */}
              <div className="feature-row-2">
                <div className="why-us-text-container">
                  <div className="feature-heading left-aligned">
                    <h3 className="h2 _700-bold" data-testid="feature-rates-heading">Know Your Estimated Payout Before You Submit</h3>
                  </div>
                  <div className="why-us-para">
                    <p className="main-body-text-variant-1 _500-medium" data-testid="feature-rates-copy">
                      Check the latest payout rate for your eligible gift card before starting your
                      submission. Select your brand, enter the card value, and see an estimated
                      payout based on the current exchange rate.
                    </p>
                  </div>
                  <div className="feature-btn-wrapper">
                    <Link to="/rates" className="feature-cta-primary" data-testid="feature-rates-btn">
                      Check Live Rates
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginLeft: 6 }}>
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </Link>
                  </div>
                </div>
                <figure className="why-us-right-image-wrapper">
                  <picture>
                    <source srcSet="/jumbo/images/cards-status.webp" type="image/webp" />
                    <img src="/jumbo/images/cards-status.png" loading="lazy" alt="Cards status dashboard" className="why-us-right-image" data-testid="cards-status-image" />
                  </picture>
                </figure>
              </div>

              {/* Row 3 — get paid your way */}
              <div className="feature-row-3">
                <figure className="why-us-left-image-wrapper">
                  <picture>
                    <source srcSet="/jumbo/images/total-received.webp" type="image/webp" />
                    <img src="/jumbo/images/total-received.png" loading="lazy" alt="Total received payout" className="why-us-left-image" data-testid="total-received-image" />
                  </picture>
                </figure>
                <div className="why-us-text-container right-side-text">
                  <div className="feature-heading left-aligned">
                    <h3 className="h2 _700-bold" data-testid="feature-payout-heading">Choose How You'd Like to Get Paid</h3>
                  </div>
                  <div className="why-us-para">
                    <p className="main-body-text-variant-1 _500-medium" data-testid="feature-payout-copy">
                      Select your preferred payout method during submission. Once your gift card has
                      been successfully verified and approved, we'll send your payout using the payment
                      option you selected.
                    </p>
                  </div>
                  <div className="feature-list-div">
                    <div className="update-list-wrappper">
                      {PAYOUT_CHECKS.map((c) => (
                        <div className="why-us-list-row" key={c}>
                          <figure className="check-icon-wrapper">
                            <img src="/jumbo/images/checkamrk.svg" loading="lazy" alt="" className="check-icon" />
                          </figure>
                          <div>{c}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="feature-btn-wrapper">
                    <Link to="/submit" className="feature-cta-primary" data-testid="feature-submit-btn">
                      Sell a Gift Card
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginLeft: 6 }}>
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Security (blue band) ─────────────────────── */}
      <section className="security-section" data-testid="home-security">
        <div className="main-container">
          <div className="security-container">
            <div className="security-heading">
              <h2 className="h2 _700-bold">A brand-new level of security</h2>
            </div>
            <div className="security-card-wrapper">
              {SECURITY_CARDS.map((c, i) => (
                <div className={`security-card${i === 1 ? " middle-block" : ""}`} key={c.title}>
                  <div className="security-card-image">
                    <img src={c.img} loading="lazy" alt={c.title} />
                  </div>
                  <div className="security-card-title"><div className="h5 _700-bold">{c.title}</div></div>
                  <div className="security-card-para"><div>{c.text}</div></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 5. Accepted brands + Rate highlights (linked band) ───────────────────── */}
      <section
        style={{
          background: "linear-gradient(180deg, #f5f7ff 0%, #eef2ff 55%, #ffffff 100%)",
          position: "relative",
          overflow: "hidden",
          paddingTop: 96,
          paddingBottom: 32,
        }}
        data-testid="home-brands"
      >
        {/* soft decorative glows */}
        <div aria-hidden="true" style={{ position: "absolute", top: -140, left: "-10%", width: 480, height: 480, background: "radial-gradient(closest-side, rgba(52,106,248,.18), rgba(52,106,248,0))", filter: "blur(8px)", pointerEvents: "none" }} />
        <div aria-hidden="true" style={{ position: "absolute", top: 40, right: "-8%", width: 420, height: 420, background: "radial-gradient(closest-side, rgba(151,124,255,.14), rgba(151,124,255,0))", filter: "blur(8px)", pointerEvents: "none" }} />

        <div className="main-container w-container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ textAlign: "center", maxWidth: 760, margin: "0 auto 52px" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 16 }}>
              Accepted Gift Card Brands
            </div>
            <h2 className="h2 _700-bold" style={{ color: "#0b0d17" }}>
              Sell Gift Cards From <span style={{ color: "#346af8" }}>70+ Popular Brands</span>
            </h2>
            <p style={{ marginTop: 16, color: "#4a4d5f", fontSize: 16.5, lineHeight: 1.6 }}>
              Browse a wide range of eligible retail, dining, entertainment, and travel gift cards accepted by GC SPARK.
            </p>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 12, maxWidth: 960, margin: "0 auto" }}>
            {POPULAR_BRANDS.map((b) => (
              <Link
                key={b}
                to="/accepted-cards"
                data-testid={`home-brand-chip-${b.toLowerCase().replace(/\s+/g, "-")}`}
                className="home-brand-chip"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 20px 11px 12px",
                  borderRadius: 90,
                  background: "#fff",
                  border: "1px solid #e6e8f2",
                  color: "#0b0d17",
                  fontWeight: 700,
                  fontSize: 14.5,
                  textDecoration: "none",
                  transition: "all .2s ease",
                  boxShadow: "0 6px 16px -8px rgba(41,45,85,.14)",
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #eaf0ff, #dbe4ff)",
                    color: "#346af8",
                    fontSize: 11.5,
                    fontWeight: 800,
                    letterSpacing: 0,
                  }}
                >
                  {b[0]}
                </span>
                {b}
              </Link>
            ))}
          </div>

          <div style={{ marginTop: 38, display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
            <Link to="/accepted-cards" className="primary-button w-button" data-testid="brands-see-all" style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              View All Accepted Brands
              <span aria-hidden="true">→</span>
            </Link>
            <p style={{ maxWidth: 720, textAlign: "center", fontSize: 13, color: "#7a7d92", lineHeight: 1.6, margin: 0 }} data-testid="brands-disclaimer">
              <strong style={{ color: "#4a4d5f" }}>Brand Disclaimer:</strong> GC SPARK is an independent gift card exchange platform.
              The brands displayed on this website are referenced only to identify gift cards that may be accepted. Unless
              expressly stated, GC SPARK is not affiliated with, sponsored by, endorsed by, or officially associated with
              those brands.
            </p>
          </div>
        </div>
        <style>{`
          .home-brand-chip:hover { border-color: #346af8; color: #346af8; transform: translateY(-2px); box-shadow: 0 14px 28px -14px rgba(52,106,248,.38); }
          @media (max-width: 640px) {
            .home-brands-scroller { display: flex !important; flex-wrap: nowrap !important; overflow-x: auto; padding-bottom: 8px; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
            .home-brands-scroller::-webkit-scrollbar { display: none; }
            .home-brands-scroller > a { flex: 0 0 auto !important; }
          }
        `}</style>
      </section>

      {/* ── 6. Current Rate Highlights ───────────────────── */}
      {topRates.length > 0 && (
        <section
          style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f7f9ff 100%)",
            paddingTop: 24,
            paddingBottom: 96,
            position: "relative",
            overflow: "hidden",
          }}
          data-testid="home-top-rates"
        >
          <div aria-hidden="true" style={{ position: "absolute", bottom: -160, left: "50%", transform: "translateX(-50%)", width: 720, height: 340, background: "radial-gradient(closest-side, rgba(52,106,248,.10), rgba(52,106,248,0))", filter: "blur(8px)", pointerEvents: "none" }} />

          <div className="main-container w-container" style={{ position: "relative", zIndex: 1 }}>
            <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 44px" }}>
              <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 16 }}>
                Current Rate Highlights
              </div>
              <h2 className="h2 _700-bold" style={{ color: "#0b0d17" }}>
                Explore <span style={{ color: "#346af8" }}>Current Gift Card Rates</span>
              </h2>
              <p style={{ marginTop: 16, color: "#4a4d5f", fontSize: 16.5, lineHeight: 1.6 }}>
                View estimated payout rates for selected gift card brands. Final eligibility and payout amounts are confirmed after verification and approval.
              </p>
            </div>

            <div className="home-rate-grid">
              {topRates.map((b, idx) => {
                const accents = ["#346af8", "#7c5cff", "#0ea5e9", "#22c55e"];
                const badges = ["Popular", "Receipt may apply", "Updated recently", "Popular"];
                const accent = accents[idx % accents.length];
                const badge = badges[idx % badges.length];
                const rate = Math.round(b.rates?.receipt || b.rate || 0);
                return (
                  <Link
                    key={b.slug || b.name}
                    to={`/rates?brand=${encodeURIComponent(b.slug || (b.name || "").toLowerCase())}`}
                    data-testid={`home-rate-${b.slug || b.name}`}
                    className="home-rate-card"
                  >
                    <span className="home-rate-accent" style={{ background: accent }} />
                    <div className="home-rate-head">
                      <span className="home-rate-icon" style={{ background: `${accent}14`, color: accent }}>
                        <Gift style={{ width: 18, height: 18 }} />
                      </span>
                      <span className="home-rate-badge">{badge}</span>
                    </div>
                    <div className="home-rate-name">{b.name}</div>
                    <div className="home-rate-value-row">
                      <span className="home-rate-value" style={{ color: accent }}>Up&nbsp;to&nbsp;{rate}%</span>
                      <span className="home-rate-value-sub">estimated rate</span>
                    </div>
                    <div className="home-rate-meta">
                      <div><span className="home-rate-meta-l">Eligible values</span><span className="home-rate-meta-v">${b.min}–${b.max}</span></div>
                      <div><span className="home-rate-meta-l">Requirements</span><span className="home-rate-meta-v">Receipt may apply</span></div>
                    </div>
                    <div className="home-rate-note">Rates may vary. Final payout confirmed after review.</div>
                    <div className="home-rate-cta">Check this rate <span aria-hidden="true">→</span></div>
                  </Link>
                );
              })}
            </div>

            <div style={{ marginTop: 40, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <Link to="/rates" className="primary-button w-button" data-testid="rates-check-all">Check All Live Rates</Link>
              <Link to="/accepted-cards" data-testid="rates-see-brands" style={{ color: "#346af8", fontWeight: 600, textDecoration: "none", fontSize: 14.5 }}>
                See all accepted brands →
              </Link>
            </div>
          </div>

          <style>{`
            .home-rate-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 22px; }
            .home-rate-card {
              position: relative;
              display: flex;
              flex-direction: column;
              background: #fff;
              border: 1px solid #eaeaf0;
              border-radius: 26px;
              padding: 26px 24px 22px;
              text-decoration: none;
              color: #0b0d17;
              box-shadow: 0 24px 44px -32px rgba(11,13,23,.22);
              overflow: hidden;
              transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
            }
            .home-rate-card:hover { transform: translateY(-6px); border-color: #cfd6f5; box-shadow: 0 30px 60px -30px rgba(52,106,248,.35); }
            .home-rate-accent { position: absolute; top: 0; left: 0; right: 0; height: 4px; }
            .home-rate-head { display: flex; align-items: center; justify-content: space-between; margin-top: 4px; margin-bottom: 18px; }
            .home-rate-icon { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 12px; }
            .home-rate-badge { font-size: 10.5px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #4a4d5f; background: #f2f4fb; padding: 5px 10px; border-radius: 90px; }
            .home-rate-name { font-family: Satoshi, sans-serif; font-size: 18px; font-weight: 700; color: #0b0d17; margin-bottom: 6px; }
            .home-rate-value-row { display: flex; align-items: baseline; gap: 10px; margin-bottom: 18px; }
            .home-rate-value { font-family: Satoshi, sans-serif; font-size: 36px; font-weight: 800; letter-spacing: -0.02em; line-height: 1; white-space: nowrap; }
            .home-rate-value-sub { font-size: 12px; color: #9a93a6; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; }
            .home-rate-meta { display: flex; flex-direction: column; gap: 8px; padding: 14px 0; border-top: 1px solid #f0f1f7; border-bottom: 1px solid #f0f1f7; margin-bottom: 14px; }
            .home-rate-meta > div { display: flex; align-items: center; justify-content: space-between; font-size: 13px; }
            .home-rate-meta-l { color: #9a93a6; font-weight: 500; }
            .home-rate-meta-v { color: #0b0d17; font-weight: 700; }
            .home-rate-note { font-size: 12px; color: #9a93a6; line-height: 1.5; margin-bottom: 14px; }
            .home-rate-cta { font-size: 14px; font-weight: 700; color: #346af8; display: inline-flex; align-items: center; gap: 6px; transition: gap .2s ease; }
            .home-rate-card:hover .home-rate-cta { gap: 10px; }
            @media (max-width: 1024px) { .home-rate-grid { grid-template-columns: repeat(2, 1fr); } }
            @media (max-width: 560px) {
              .home-rate-grid { grid-template-columns: 1fr; gap: 16px; }
              .home-rate-value { font-size: 32px; }
              .home-rate-card { border-radius: 22px; padding: 22px 20px 20px; }
            }
          `}</style>
        </section>
      )}

      {/* ── 7. Estimated Payout Time (blue section) ─────── */}
      <PayoutTimeSection />

      {/* ── 8. Customer Reviews (trust-first redesign) ─────────────────── */}
      <section
        data-testid="home-reviews"
        style={{
          position: "relative",
          overflow: "hidden",
          background: "linear-gradient(180deg, #f6f8ff 0%, #eef3ff 55%, #ffffff 100%)",
          padding: "96px 0 88px",
        }}
      >
        <div className="anchorlink-space" id="Reviews" />
        {/* soft floating blurred circles */}
        <div aria-hidden="true" style={{ position: "absolute", top: -180, left: "-8%", width: 520, height: 520, background: "radial-gradient(closest-side, rgba(52,106,248,.14), rgba(52,106,248,0))", filter: "blur(8px)", pointerEvents: "none" }} />
        <div aria-hidden="true" style={{ position: "absolute", bottom: -160, right: "-10%", width: 460, height: 460, background: "radial-gradient(closest-side, rgba(124,92,255,.12), rgba(124,92,255,0))", filter: "blur(8px)", pointerEvents: "none" }} />

        <div className="main-container w-container" style={{ position: "relative", zIndex: 1 }}>
          {/* Heading */}
          <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 52px" }} className="rv-heading-block">
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 16 }}>
              Customer Reviews
            </div>
            <h2 className="h2 _700-bold" style={{ color: "#0b0d17" }}>
              Trusted by Gift Card Sellers <span style={{ color: "#346af8" }}>Across the U.S.</span>
            </h2>
            <p style={{ marginTop: 16, color: "#4a4d5f", fontSize: 16.5, lineHeight: 1.6 }}>
              Read what customers have to say about selling their gift cards through GC SPARK. Every review reflects a real customer experience with our submission, verification, and payout process.
            </p>
          </div>

          {/* Row 1: Overall Rating Card + Featured Review */}
          <div className="rv-top-grid">
            {/* Overall Rating Card */}
            <div className="rv-rating-card" data-testid="reviews-overall-card">
              <div className="rv-rating-pill">
                <Stars value={4.8} size={16} />
                <span style={{ fontSize: 13, fontWeight: 700, color: "#0b0d17" }}>4.8/5</span>
              </div>
              <div className="rv-rating-big">4.8</div>
              <div className="rv-rating-label">Average Rating</div>
              <div className="rv-rating-sub">Based on verified customer reviews</div>
              <div className="rv-breakdown">
                {RATING_BREAKDOWN.map((r) => (
                  <div key={r.label} className="rv-breakdown-row">
                    <span className="rv-breakdown-label">{r.label}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Stars value={r.score} size={12} />
                      <span className="rv-breakdown-score">{r.score.toFixed(1)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Featured Review */}
            <div className="rv-featured" data-testid="reviews-featured">
              <Quote className="rv-featured-quote" strokeWidth={2.4} aria-hidden="true" />
              <div className="rv-featured-stars">
                <Stars value={FEATURED_REVIEW.rating} size={16} />
              </div>
              <p className="rv-featured-text">{FEATURED_REVIEW.text}</p>
              <div className="rv-featured-footer">
                <div className="rv-avatar" style={{ background: "linear-gradient(135deg, #346af8, #7c5cff)", color: "#fff" }}>
                  {FEATURED_REVIEW.initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="rv-featured-name">
                    {FEATURED_REVIEW.name}
                    <span className="rv-verified" title="Verified Customer">
                      <BadgeCheck size={14} strokeWidth={2.4} /> Verified Customer
                    </span>
                  </div>
                  <div className="rv-featured-meta">
                    {FEATURED_REVIEW.brand} · Paid via {FEATURED_REVIEW.payout} · {FEATURED_REVIEW.date}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Horizontal review carousel */}
          <div className="rv-carousel-wrap" data-testid="reviews-carousel">
            <div className="rv-carousel">
              {REVIEWS.map((r, i) => (
                <article key={r.name} className="rv-card" data-testid={`review-${i}`}>
                  <Quote className="rv-card-quote" strokeWidth={2.4} aria-hidden="true" />
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div className="rv-avatar">{r.initials}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: "#0b0d17", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {r.name}
                        <span className="rv-verified-mini" title="Verified">
                          <BadgeCheck size={12} strokeWidth={2.4} />
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "#7a7d92", marginTop: 2 }}>{r.location}</div>
                    </div>
                    <Stars value={r.rating} size={12} />
                  </div>
                  <div className="rv-card-brand">{r.brand}</div>
                  <p className="rv-card-text">{r.text}</p>
                  <div className="rv-card-date">{r.date}</div>
                </article>
              ))}
            </div>
          </div>

          {/* Trust strip */}
          <div className="rv-trust" data-testid="reviews-trust-strip">
            <div className="rv-trust-item">
              <Stars value={4.8} size={14} />
              <span><strong>4.8</strong> Average Rating</span>
            </div>
            <span className="rv-trust-divider" aria-hidden="true" />
            <div className="rv-trust-item">
              <BadgeCheck size={16} strokeWidth={2.4} style={{ color: "#346af8" }} />
              <span>Verified Customer Reviews</span>
            </div>
            <span className="rv-trust-divider" aria-hidden="true" />
            <div className="rv-trust-item">
              <Lock size={15} strokeWidth={2.2} style={{ color: "#346af8" }} />
              <span>Secure Transactions</span>
            </div>
            <span className="rv-trust-divider" aria-hidden="true" />
            <div className="rv-trust-item">
              <Headphones size={16} strokeWidth={2.2} style={{ color: "#346af8" }} />
              <span>Dedicated Support</span>
            </div>
          </div>
        </div>

        <style>{`
          .rv-top-grid { display: grid; grid-template-columns: 0.9fr 1.5fr; gap: 22px; margin-bottom: 44px; }
          .rv-rating-card {
            background: linear-gradient(160deg, #346af8 0%, #4f7dff 55%, #7c5cff 100%);
            color: #fff;
            border-radius: 26px;
            padding: 30px 28px;
            box-shadow: 0 30px 60px -30px rgba(52,106,248,.55);
            position: relative;
            overflow: hidden;
          }
          .rv-rating-card::after {
            content: "";
            position: absolute;
            inset: auto -20% -40% auto;
            width: 260px; height: 260px;
            background: radial-gradient(closest-side, rgba(255,255,255,.22), rgba(255,255,255,0));
            filter: blur(4px);
            pointer-events: none;
          }
          .rv-rating-card > * { position: relative; z-index: 1; }
          .rv-rating-pill { display: inline-flex; align-items: center; gap: 10px; background: rgba(255,255,255,.95); color: #0b0d17; padding: 6px 12px; border-radius: 90px; width: fit-content; }
          .rv-rating-big { font-family: Satoshi, sans-serif; font-size: 64px; font-weight: 800; line-height: 1; margin: 22px 0 6px; letter-spacing: -0.03em; color: #fff; }
          .rv-rating-label { font-size: 14.5px; font-weight: 700; color: #fff; margin-bottom: 4px; }
          .rv-rating-sub { font-size: 13px; color: rgba(255,255,255,.78); margin-bottom: 22px; }
          .rv-breakdown { border-top: 1px solid rgba(255,255,255,.22); padding-top: 18px; display: flex; flex-direction: column; gap: 10px; }
          .rv-breakdown-row { display: flex; align-items: center; justify-content: space-between; }
          .rv-breakdown-label { font-size: 13.5px; font-weight: 500; color: rgba(255,255,255,.86); }
          .rv-breakdown-score { font-size: 13.5px; font-weight: 700; color: #fff; }

          .rv-featured {
            background: #fff;
            border-radius: 26px;
            border: 1px solid #eaeaf0;
            box-shadow: 0 30px 60px -34px rgba(11,13,23,.22);
            padding: 34px 34px 28px;
            position: relative;
            overflow: hidden;
          }
          .rv-featured::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #346af8, #7c5cff); }
          .rv-featured-quote { position: absolute; top: 22px; right: 24px; color: #dbe4ff; width: 44px; height: 44px; }
          .rv-featured-stars { margin-bottom: 14px; }
          .rv-featured-text { font-family: Satoshi, sans-serif; font-size: 20px; line-height: 1.55; color: #0b0d17; font-weight: 500; margin: 0 0 24px; }
          .rv-featured-footer { display: flex; align-items: center; gap: 14px; padding-top: 20px; border-top: 1px solid #f0f1f7; }
          .rv-featured-name { font-size: 15px; font-weight: 700; color: #0b0d17; display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
          .rv-featured-meta { font-size: 13px; color: #7a7d92; margin-top: 3px; }
          .rv-verified { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 90px; background: #eafaf1; color: #16a34a; font-size: 11.5px; font-weight: 700; }
          .rv-verified-mini { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; background: #eafaf1; color: #16a34a; }

          .rv-avatar {
            width: 44px; height: 44px;
            border-radius: 50%;
            background: linear-gradient(135deg, #eaf0ff, #dbe4ff);
            color: #346af8;
            display: inline-flex; align-items: center; justify-content: center;
            font-family: Satoshi, sans-serif; font-weight: 800; font-size: 15px;
            flex-shrink: 0;
          }

          .rv-carousel-wrap {
            position: relative;
            margin: 0 -18px 32px;
            padding: 4px 18px 18px;
          }
          .rv-carousel {
            display: grid;
            grid-auto-flow: column;
            grid-auto-columns: minmax(320px, 1fr);
            gap: 20px;
            overflow-x: auto;
            padding: 6px 4px 20px;
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .rv-carousel::-webkit-scrollbar { display: none; }
          .rv-card {
            background: #fff;
            border: 1px solid #eaeaf0;
            border-radius: 22px;
            padding: 22px 22px 20px;
            box-shadow: 0 18px 34px -24px rgba(11,13,23,.18);
            scroll-snap-align: start;
            position: relative;
            overflow: hidden;
            transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
            display: flex; flex-direction: column; gap: 14px;
          }
          .rv-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: #346af8; opacity: .85; }
          .rv-card:hover { transform: translateY(-6px); border-color: #cfd6f5; box-shadow: 0 30px 60px -30px rgba(52,106,248,.35); }
          .rv-card-quote { position: absolute; top: 14px; right: 14px; color: #eef2ff; width: 30px; height: 30px; }
          .rv-card-brand { font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; color: #346af8; }
          .rv-card-text { font-size: 14.5px; line-height: 1.6; color: #4a4d5f; margin: 0; }
          .rv-card-date { font-size: 12px; color: #9a93a6; margin-top: auto; padding-top: 6px; border-top: 1px dashed #f0f1f7; }

          .rv-trust {
            display: flex; align-items: center; justify-content: center; flex-wrap: wrap;
            gap: 22px;
            background: #ffffff;
            border: 1px solid #eaeaf0;
            border-radius: 90px;
            padding: 14px 26px;
            box-shadow: 0 16px 32px -20px rgba(11,13,23,.14);
            max-width: 940px;
            margin: 0 auto;
          }
          .rv-trust-item { display: inline-flex; align-items: center; gap: 8px; font-size: 14px; color: #4a4d5f; font-weight: 600; }
          .rv-trust-item strong { color: #0b0d17; font-weight: 800; }
          .rv-trust-divider { width: 4px; height: 4px; border-radius: 50%; background: #cfd6f5; }

          @keyframes rvFadeUp {
            from { opacity: 0; transform: translateY(14px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .rv-featured, .rv-rating-card, .rv-card, .rv-trust { animation: rvFadeUp .5s ease-out both; }
          .rv-featured { animation-delay: .05s; }
          .rv-card:nth-child(1) { animation-delay: .1s; }
          .rv-card:nth-child(2) { animation-delay: .16s; }
          .rv-card:nth-child(3) { animation-delay: .22s; }
          .rv-card:nth-child(4) { animation-delay: .28s; }

          @media (max-width: 900px) {
            .rv-top-grid { grid-template-columns: 1fr; }
            .rv-carousel { grid-auto-columns: 85%; }
            .rv-featured-text { font-size: 17px; }
            .rv-rating-big { font-size: 54px; }
            .rv-trust { border-radius: 22px; padding: 16px 20px; gap: 12px; }
            .rv-trust-divider { display: none; }
            .rv-trust-item { flex: 1 0 45%; justify-content: flex-start; }
          }
          @media (max-width: 480px) {
            .rv-carousel { grid-auto-columns: 92%; }
            .rv-featured { padding: 26px 22px 22px; }
            .rv-featured-quote { width: 34px; height: 34px; top: 18px; right: 18px; }
            .rv-rating-card { padding: 26px 22px; }
          }
        `}</style>
      </section>

      {/* ── 9. FAQ ──────────────────────────────────────── */}
      <section className="faq-section" data-testid="home-faq">
        <div className="main-container">
          <div className="faq-wrapper">
            <div className="faq-heading-div">
              <h4 className="h2 _700-bold">Frequently asked questions</h4>
            </div>
            <div className="faq-para-div">
              <div className="main-body-text-variant-1 _500-medium">
                Quick answers — or <Link to="/faq" style={{ color: "#346af8" }}>see the full FAQ</Link>
              </div>
            </div>
            <div className="faq-accordion-wrapper">
              <div className="faq-accordion-container">
                <div className="c-faq">
                  {FAQS.map((f, i) => (
                    <div className="c-faq-item" key={f.q} data-testid={`home-faq-${i}`}>
                      <div
                        className="c-faq-q"
                        role="button"
                        tabIndex={0}
                        aria-expanded={openFaq === i}
                        onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                        onKeyDown={(e) => e.key === "Enter" && setOpenFaq(openFaq === i ? -1 : i)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="c-faq-q-text"><h5 className="h6 _500-medium">{f.q}</h5></div>
                        <div className="c-faq-icon">
                          <div className="boss-faq-toggle">{openFaq === i ? "−" : "+"}</div>
                        </div>
                      </div>
                      {openFaq === i && (
                        <div className="c-faq-a" style={{ height: "auto" }}>
                          <div className="c-faq-divider"><div className="faq-divider" /></div>
                          <div className="c-faq-a-text">
                            <p className="main-body-text-variant-2 _500-medium">{f.a}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 10. CTA band ────────────────────────────────── */}
      <section className="boss-section" style={{ paddingTop: 20 }} data-testid="home-cta">
        <div className="boss-container">
          <div className="boss-cta-band">
            <h2>Start with GC SPARK</h2>
            <p>It only takes a few minutes to get an estimate — final payout is confirmed after verification, typically within 24 hours of approval. No account required.</p>
            <Link to="/submit" className="boss-btn" data-testid="cta-sell-btn">
              Get my payout quote
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
